import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "react-query";
import { useRouter } from "next/router";
import { useApi } from "./useApi";
import { useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
import { ASSISTANT_SESSIONS_QUERY_KEY, fetchAssistantSessions } from "@/lib/assistant-sessions";

/**
 * Prefetch Codesmith session list on mount so data is already cached
 * when the user navigates to Codesmith. Uses requestIdleCallback to
 * avoid competing with critical first-paint resources.
 *
 * The query key must match the one used by useSessionPools:
 * [ASSISTANT_SESSIONS_QUERY_KEY, orgName]. Otherwise react-query won't
 * consider the prefetched data a match for the consumer's useQuery and
 * will refetch. We also pass the resolved installation ID to
 * fetchAssistantSessions so the API response is scoped to the same org
 * the consumer expects — otherwise we'd cache cross-org data.
 */
export function usePrefetchAssistantSessions() {
  const api = useApi();
  const queryClient = useQueryClient();
  const router = useRouter();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  const urlOrg = router.query.org as string | undefined;
  const orgName = urlOrg ?? connectedGithubOrgs?.active_org_name ?? null;
  const currentInstallationId = useMemo<number | null>(() => {
    if (!connectedGithubOrgs?.installations || !orgName) return null;
    const inst = connectedGithubOrgs.installations.find(i => i.account.login.toLowerCase() === orgName.toLowerCase());
    return inst?.blacksmith_id ?? null;
  }, [orgName, connectedGithubOrgs]);

  // useApi() returns a new Axios instance every render, so we stabilize the
  // reference with a ref to avoid re-triggering the effect (and cancelling
  // the pending idle callback) on each re-render.
  const apiRef = useRef(api);
  apiRef.current = api;
  useEffect(() => {
    // Wait until we've resolved an org name AND its installation ID so the
    // prefetched data lands under the same key useSessionPools reads from
    // AND is scoped to the same installation the consumer will fetch.
    if (!orgName || !currentInstallationId) return;
    const sessionQueryKey = [ASSISTANT_SESSIONS_QUERY_KEY, orgName];
    const doPrefetch = () => {
      const existing = queryClient.getQueryData(sessionQueryKey);
      if (existing) return;
      queryClient.prefetchQuery(sessionQueryKey,
      // Options must match useSessionPools' fetch exactly: the prefetched
      // response seeds that hook's pools, so a mismatch (e.g. keeping
      // automation run sessions here) would flash rows the consumer's own
      // fetch later removes.
      () => fetchAssistantSessions(apiRef.current, currentInstallationId, null, {
        excludeAutomationSessions: true
      }), {
        staleTime: 30 * 1000
      });
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = (window as Window).requestIdleCallback(doPrefetch, {
        timeout: 200
      });
      return () => (window as Window).cancelIdleCallback(id);
    } else {
      const id_0 = setTimeout(doPrefetch, 50);
      return () => clearTimeout(id_0);
    }
  }, [queryClient, orgName, currentInstallationId]);
}